import React, { useEffect, useRef, useState } from 'react';
import { styles } from './styles';
import { prefersReducedMotion } from '../../utils';

interface SectionWrapperProps {
  id?: string;
  index?: string;
  label: string;
  heading: string;
  sub?: string;
  children?: React.ReactNode;
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({ id, index, label, heading, sub, children }) => {
  const ref = useRef<HTMLElement | null>(null);
  const dirRef = useRef<'down' | 'up'>('down');
  const lastY = useRef(0);
  const [shown, setShown] = useState(false);
  const [dir, setDir] = useState<'down' | 'up'>('down');

  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    lastY.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y !== lastY.current) dirRef.current = y > lastY.current ? 'down' : 'up';
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Capture the scroll direction at the moment the section flips, so the
    // reveal plays in that direction (mirrored when scrolling up).
    const obs = new IntersectionObserver(
      (entries) => {
        setDir(dirRef.current);
        setShown(entries[0].isIntersecting);
      },
      { rootMargin: '-20% 0px -20% 0px', threshold: 0 },
    );
    obs.observe(el);
    return () => {
      window.removeEventListener('scroll', onScroll);
      obs.disconnect();
    };
  }, []);

  const STEP = 90;
  const LAST = 3;
  const d = shown
    ? { op: '0.55s', tr: '0.6s', line: '0.7s', content: '0.6s' }
    : { op: '0.4s', tr: '0.45s', line: '0.5s', content: '0.45s' };
  const offX = dir === 'up' ? 16 : -16; // slide from the direction of travel
  const origin = dir === 'up' ? 'right' : 'left'; // hairline draws toward travel
  // stagger top→bottom scrolling down, bottom→top scrolling up
  const delay = (order: number) => (dir === 'down' ? order : LAST - order) * STEP;

  const rise = (order: number): React.CSSProperties => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateX(0)' : `translateX(${offX}px)`,
    transition: `opacity ${d.op} ease ${delay(order)}ms, transform ${d.tr} cubic-bezier(0.2,0.7,0.2,1) ${delay(order)}ms`,
  });

  return (
    <section id={id} ref={ref} className="section-wrap" style={styles.section}>
      <div style={styles.band}>
        <span
          style={{
            ...styles.bandLine,
            transformOrigin: origin,
            transform: shown ? 'scaleX(1)' : 'scaleX(0)',
            transition: `transform ${d.line} cubic-bezier(0.2,0.7,0.2,1) ${delay(0)}ms`,
          }}
        />
        <div style={{ ...styles.bandRow, ...rise(0) }}>
          {index && <span style={styles.index}>{index}</span>}
          <span style={styles.label}>{label}</span>
        </div>
      </div>
      <h2 style={{ ...styles.heading, ...rise(1) }}>{heading}</h2>
      {sub && <p style={{ ...styles.sub, ...rise(2) }}>{sub}</p>}
      <div style={{ opacity: shown ? 1 : 0, transition: `opacity ${d.content} ease ${delay(3)}ms` }}>
        {children}
      </div>
    </section>
  );
};

export default SectionWrapper;
